import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Leaf } from "lucide-react";
import { PRODUCTS, IMAGES } from "../constants/kalorenceData";
import { Reveal } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";

const FILTERS = [
  { key: "all", label: "All Pieces" },
  { key: "dupattas", label: "Dupattas & Stoles" },
  { key: "totes", label: "Tote Bags" },
  { key: "apparel", label: "Apparel" },
  { key: "bandanas", label: "Bandanas" },
];

export const Shop = ({ onAdd, onQuickView, initialCategory = "all" }) => {
  const [filter, setFilter] = useState(initialCategory);

  useEffect(() => {
    setFilter(initialCategory);
  }, [initialCategory]);

  const filtered = useMemo(
    () =>
      filter === "all"
        ? PRODUCTS
        : PRODUCTS.filter((p) => p.categoryKey === filter),
    [filter]
  );

  return (
    <div data-testid="shop-page" className="bg-[#F7F5F0] min-h-screen">
      {/* Header */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <img
          src={IMAGES.img_13}
          alt="Ochre botanical linen"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2B3A2F]/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-[#F7F5F0]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#E2DACF] mb-3 flex items-center gap-2">
              <Leaf className="w-4 h-4 text-[#C47D65]" /> The Studio Collection
            </p>
            <h1 className="font-cormorant text-5xl sm:text-6xl font-light leading-tight">
              Each piece, unrepeatable
            </h1>
            <p className="mt-4 max-w-xl text-[#F7F5F0]/85 font-light">
              No two are alike — every garment carries the memory of a single day's leaves.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <div className="sticky top-20 z-30 bg-[#F7F5F0]/90 backdrop-blur-md border-b border-[#E2DACF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex gap-2 sm:gap-3 overflow-x-auto">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`shop-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`whitespace-nowrap px-4 sm:px-5 py-2 text-xs tracking-[0.12em] uppercase font-medium transition-all ${
                filter === f.key
                  ? "bg-[#2B3A2F] text-[#F7F5F0]"
                  : "text-[#5C6B5F] border border-[#E2DACF] hover:border-[#2B3A2F] hover:text-[#2B3A2F]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p
            data-testid="shop-product-count"
            className="text-xs tracking-[0.15em] uppercase text-[#5C6B5F] mb-8 font-semibold"
          >
            {filtered.length} piece{filtered.length !== 1 ? "s" : ""}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-12 sm:gap-x-8">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <ProductCard product={p} onAdd={onAdd} onQuickView={onQuickView} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
